import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import appConfig from '../config';

interface AppConfig {
  debug: boolean;
  app_name: string;
  sample_questions?: string[];
  ui_theme?: {
    app_background: string;
    sample_question_background: string;
    sample_question_text: string;
    sample_question_border: string;
    sample_question_hover_background: string;
    sample_question_hover_text: string;
    sample_question_hover_border: string;
    recording_button_background: string;
    recording_button_hover_background: string;
    recording_button_recording_background: string;
    recording_button_recording_hover_background: string;
    text_input_background: string;
    text_input_text: string;
    text_input_border: string;
    text_input_border_focus: string;
    send_button_background: string;
    send_button_hover_background: string;
    image_button_background: string;
    image_button_hover_background: string;
    image_search_button_background: string;
    image_search_button_hover_background: string;
    image_search_button_icon_color: string;
    image_search_button_border: string;
    image_search_button_disabled_background: string;
    image_search_button_disabled_icon: string;
    image_search_button_active_background: string;
    image_search_drag_drop_background: string;
    image_search_drag_drop_border: string;
    image_search_drag_drop_hover_border: string;
    image_search_preview_background: string;
    image_search_preview_border: string;
    new_conversation_button_background: string;
    new_conversation_button_hover_background: string;
    user_message_background: string;
    user_message_text: string;
    user_message_border: string;
    user_icon_background: string;
    user_icon_color: string;
    assistant_icon_background: string;
    assistant_icon_color: string;
    assistant_message_background: string;
    assistant_message_text: string;
    assistant_message_border: string;
    user_message_type_background: string;
    assistant_message_type_background: string;
    sources_button_text: string;
    sources_button_icon: string;
    sources_button_background: string;
    sources_button_border: string;
    sources_button_count_text: string;
    sources_button_count_background: string;    play_button_text: string;
    play_button_icon: string;
    play_button_background: string;
    play_button_border: string;    citations_flyout_title_text: string;
    citations_flyout_title_icon: string;
    citations_flyout_title_background: string;
    citations_text_color: string;
    citations_number_color: string;
    citations_content_color: string;
    citations_url_color: string;    audio_flyout_title_text: string;
    audio_flyout_title_icon: string;
    audio_flyout_title_background: string;
    image_carousel_title_text: string;
    image_carousel_title_background: string;
    image_carousel_title_font_size: string;
  };  ui_text?: {
    welcome_message: string;
    welcome_message_font_weight: string;
    welcome_message_font_style: string;
    welcome_message_font_size: string;
    welcome_message_color: string;
    welcome_message_text_align: string;
    welcome_display_mode: string;
    sample_questions_label: string;
    text_input_placeholder: string;
    send_button_label: string;
    sources_button_label: string;
    disclaimer_text: string;
    image_carousel_title: string;
    image_carousel_outfit_button_text: string;
    image_carousel_similar_button_text: string;
    image_carousel_color_label?: string;
    image_carousel_gender_label?: string;
    image_carousel_sizes_label?: string;
    image_carousel_sizes_more_text?: string;
    image_carousel_attribute_separator?: string;
    image_search_button_label: string;
    image_search_drag_drop_text: string;
    image_search_file_size_error: string;
    image_search_format_error: string;
    image_search_uploading_text: string;
    image_search_success_text: string;
    image_search_remove_text: string;
  };
  ui_tooltips?: {
    send_button_tooltip: string;
    start_recording_tooltip: string;
    new_conversation_tooltip: string;
    sources_button_tooltip: string;
    play_button_tooltip: string;
    generate_audio_tooltip: string;
    pause_audio_tooltip: string;
    copy_text_tooltip: string;
    copied_tooltip: string;
    image_search_button_tooltip: string;
    image_search_button_uploading_tooltip: string;
    image_search_button_error_tooltip: string;
    image_search_drag_drop_tooltip: string;
    image_search_remove_tooltip: string;
  };
  ui_citations_flyout?: {
    title: string;
    footer: string;
  };
  ui_behavior?: {
    enable_autoscroll: boolean;
    show_message_timestamps?: boolean;
    show_message_type?: boolean;
    show_connection_status?: boolean;
    enable_image_search_button?: boolean;
  };
  ui_enable_citations_button?: boolean;
  ui_enable_audio_controls?: boolean;
  ui_enable_copy_button?: boolean;
  image_carousel?: {
    enable_citation_based_filtering: boolean;
    desktop_visible_count?: number;
    tablet_visible_count?: number;
    mobile_visible_count?: number;
    show_button_icons?: boolean;
    outfit_button_bg_color?: string;
    outfit_button_text_color?: string;
    similar_button_bg_color?: string;
    similar_button_text_color?: string;
    show_tooltip?: boolean;
    show_price?: boolean;
    show_color?: boolean;
    show_sizes?: boolean;
    show_gender?: boolean;
    show_description?: boolean;
    enable_auto_rotate?: boolean;
    auto_rotate_interval?: number;
    display_mode?: string;  // Options: 'carousel', 'grid', 'both'
    enable_view_toggle?: boolean;
    grid_gap?: number;
    grid_products_per_page?: number;
    grid_show_title?: boolean;
  };
  product_quickview?: {
    enabled: boolean;
    show_size_chips: boolean;
    show_description: boolean;
    show_colors: boolean;
    show_color_palette: boolean;
    show_category: boolean;
    show_style: boolean;
    show_silhouette: boolean;
    show_season: boolean;
    add_to_cart_text: string;
    add_to_cart_url_template: string;
    add_to_cart_bg_color: string;
    add_to_cart_text_color: string;
    text_direction: string;
  };
  product_tiles?: {
    enabled: boolean;
    grid_columns_desktop: number;
    grid_columns_tablet: number;
    grid_columns_mobile: number;
    grid_gap: number;
    image_aspect_ratio: string;
    min_height: number;
    max_height: number;
    carousel_interval: number;
    products_per_category: number;
    auto_advance: boolean;
    show_dots: boolean;
    prefetch_on_startup: boolean;
    cache_duration: number;
    random_selection: boolean;
    categories: Array<{ category: string; display_name: string }>;
    search_strategy: string;
    max_search_results: number;
    min_products_to_display: number;
    show_tooltip: boolean;
    tooltip_text: string;
    click_question_template: string;
    swipe_enabled: boolean;
    swipe_threshold: number;
  };
}

interface ConfigContextType {
  config: AppConfig | null;
  loading: boolean;
  error: string | null;
  isConfigReady: boolean; // Indicates if config is loaded and ready to use
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

const FALLBACK_CONFIG: AppConfig = {
  debug: false,
  app_name: 'Loading...',
};

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${appConfig.apiUrl}/config`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch config: ${response.statusText}`);
        }
        
        const configData = await response.json();
        setConfig(configData);
        setError(null);
        console.log('Config loaded successfully, UI can now be fully initialized');
      } catch (err) {
        console.error('Failed to fetch config from backend:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setConfig(FALLBACK_CONFIG);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const isConfigReady = !loading && config !== null;

  return (
    <ConfigContext.Provider value={{ config, loading, error, isConfigReady }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useAppConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within a ConfigProvider');
  }
  return context;
};

// Export the AppConfig interface for use in other files
export type { AppConfig };
